import PasswordRequirementsV9 from '../../components/PasswordRequirementsV9';
import { isValidNewPasswordV9 } from '../../helpers/passwordPolicyV9';
import { isValidUsernameV108 } from '../../helpers/usernamePolicyV108';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Pagination,
  Segment,
  Statistic,
  Table,
} from 'semantic-ui-react';
import {
  API,
  isRoot,
  showError,
  showFormalPricingWarningsV87,
  showSuccess,
} from '../../helpers';
import PageSizeSelectV8, {
  readPageSizeV8,
} from '../../components/PageSizeSelectV8';
import TokensTable from '../../components/TokensTable';
import BillingDiscountProgramSettingV816 from '../../components/BillingDiscountProgramSettingV816';
import UsageAuditV8 from './UsageAuditV8';
import OrganizationBillingBookV8 from './OrganizationBillingBookV8';
import OrganizationInvitationsV108 from './OrganizationInvitationsV108';
import MemberCreditV108 from './MemberCreditV108';
import { getOrganizationCreditMetricV86 } from './organizationCreditV86';
import {
  organizationEditorIdentityV812,
  organizationEffectiveModelCountV812,
} from './organizationEditorV812';
import useOrganizationScopeV104 from './useOrganizationScopeV104';
import useOrganizationListV1116 from './useOrganizationListV1116';
import OrganizationModelExclusionsV104 from './OrganizationModelExclusionsV104';
import { OrganizationSourcesV109 } from './OrganizationSourcesV109';
import {
  allowAllCurrentOrganizationModelsV104,
  organizationModelModeV104,
} from './organizationModelsV104';
import {
  organizationAccessGroupsLabelV813,
  organizationAllowedModelsLabelV813,
  organizationGroupSummaryV813,
} from './organizationRootScopeV813';
import './Organization.css';
import {
  MemberUsageCellsV97,
  MemberUsageSummaryV97,
} from './OrganizationMemberUsageV97';
import {
  useSessionViewFieldV81,
  useSessionViewStateV81,
} from '../../hooks/useSessionViewStateV81';
import useSmartBackV81 from '../../hooks/useSmartBackV81';

const emptyOrganizationForm = {
  name: '',
  owner_username: '',
  owner_display_name: '',
  owner_password: '',
  owner_email: '',
  mode: 'prepaid',
  portal_profile: 'standard',
  billing_day: 1,
  seat_limit: 0,
  rpm_limit: 0,
  tpm_limit: 0,
  status: 'active',
  allowed_groups: [],
  excluded_models: [],
  model_access_mode: 'rules',
  log_debug_enabled: false,
  known_models: [],
  opening_credit_usd: '',
  opening_idempotency_key: '',
  opening_description: '',
};

const emptyMemberForm = {
  username: '',
  display_name: '',
  password: '',
  email: '',
  status: 'active',
  primary_group: '',
  access_groups: [],
  allowed_models: [],
  model_scope: 'inherit',
  cycle_limit_usd: '',
  quota_usd: '',
};

const scopePath = (base, groups) => {
  const query = groups
    .map((group) => `group=${encodeURIComponent(group)}`)
    .join('&');
  return query ? `${base}?${query}` : base;
};

const dropdownOptions = (values) =>
  (values || []).map((value) => ({ key: value, text: value, value }));

const billingRequestKeyV8 = (prefix) => {
  if (window.crypto?.randomUUID) {
    return prefix + ':' + window.crypto.randomUUID();
  }
  return prefix + ':' + Date.now() + ':' + Math.random().toString(16).slice(2);
};

const BillingAdjustmentModal = ({ open, organization, onClose }) => {
  const { t } = useTranslation();
  const account = organization?.account || {};
  const owner = organization?.owner || {};
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adjustment, setAdjustment] = useState({
    direction: 'credit',
    amount_usd: '',
    description: '',
  });

  const loadSummary = useCallback(async () => {
    if (!open || !account.id) return;
    setLoading(true);
    try {
      const response = await API.get(
        `/api/billing/v8/admin/accounts/${account.id}/summary`
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      setSummary(response.data.data);
    } catch (error) {
      showError(error.message || t('billing.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [account.id, open, t]);

  useEffect(() => {
    if (!open) return;
    setSummary(null);
    setAdjustment({
      direction: 'credit',
      amount_usd: '',
      description: '',
    });
    loadSummary();
  }, [loadSummary, open]);

  const submit = async () => {
    if (Number(adjustment.amount_usd) <= 0 || !adjustment.description.trim()) {
      showError(t('billing.errors.adjustment_required'));
      return;
    }
    setSaving(true);
    try {
      const response = await API.post(
        `/api/billing/v8/admin/accounts/${account.id}/adjustments`,
        {
          ...adjustment,
          idempotency_key: billingRequestKeyV8('organization-adjustment'),
        }
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showSuccess(t('billing.messages.adjusted'));
      onClose();
    } catch (error) {
      showError(error.message || t('billing.errors.adjustment'));
    } finally {
      setSaving(false);
    }
  };

  const balance = !summary
    ? '-'
    : summary.available_credit_usd == null
    ? t('organization.unlimited')
    : `$${summary.available_credit_usd}`;

  return (
    <Modal open={open} onClose={onClose} closeIcon size='small'>
      <Modal.Header>{t('organization.adjustment.title')}</Modal.Header>
      <Modal.Content>
        <div className='organization-adjustment-context-v8'>
          <div>
            <span>{t('organization.adjustment.organization')}</span>
            <strong>{account.name || '-'}</strong>
          </div>
          <div>
            <span>{t('organization.adjustment.owner')}</span>
            <strong>{owner.username || '-'}</strong>
          </div>
          <div>
            <span>{t('organization.adjustment.balance')}</span>
            <strong>{loading ? '...' : balance}</strong>
          </div>
        </div>
        <Form>
          <Form.Group widths='equal'>
            <Form.Select
              label={t('billing.admin.direction')}
              value={adjustment.direction}
              options={[
                {
                  key: 'credit',
                  value: 'credit',
                  text: t('billing.direction.credit'),
                },
                {
                  key: 'debit',
                  value: 'debit',
                  text: t('billing.direction.debit'),
                },
              ]}
              onChange={(_, { value }) =>
                setAdjustment((current) => ({
                  ...current,
                  direction: value,
                }))
              }
            />
            <Form.Input
              type='number'
              min='0'
              step='0.01'
              inputMode='decimal'
              label={t('billing.admin.amount')}
              value={adjustment.amount_usd}
              onChange={(_, { value }) =>
                setAdjustment((current) => ({
                  ...current,
                  amount_usd: value,
                }))
              }
            />
          </Form.Group>
          <Form.Input
            label={t('billing.admin.description')}
            maxLength={191}
            value={adjustment.description}
            onChange={(_, { value }) =>
              setAdjustment((current) => ({
                ...current,
                description: value,
              }))
            }
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>{t('organization.actions.cancel')}</Button>
        <Button
          primary
          icon
          labelPosition='left'
          loading={saving}
          onClick={submit}
        >
          <Icon name='dollar sign' />
          {t('organization.adjustment.submit')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const validateManagedUserV8 = (
  { username, password, displayName, email },
  { requireUsername, requirePassword, t }
) => {
  const normalizedUsername = (username || '').trim();
  if (requireUsername && !isValidUsernameV108(normalizedUsername)) {
    return t('organization.validation.username_length');
  }
  if ((requirePassword || password) && !isValidNewPasswordV9(password)) {
    return t('password_v9.error');
  }
  if (Array.from((displayName || '').trim()).length > 20) {
    return t('organization.validation.display_name_length');
  }
  if ((email || '').trim().length > 50) {
    return t('organization.validation.email_length');
  }
  return '';
};

export const OrganizationEditor = ({
  open,
  organization,
  groups,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const editing = Boolean(organization);
  const [form, setForm] = useState(emptyOrganizationForm);
  const [saving, setSaving] = useState(false);
  const [hydratedIdentity, setHydratedIdentity] = useState(null);
  const editorIdentity = organizationEditorIdentityV812(organization);
  const scope = useOrganizationScopeV104({
    open,
    hydratedIdentity,
    editorIdentity,
    groups: form.allowed_groups,
    editing,
    paused: saving,
    setForm,
  });
  const scopeModels = scope.models;

  useEffect(() => {
    if (!open) {
      setHydratedIdentity(null);
      return;
    }
    if (organization) {
      if (hydratedIdentity === editorIdentity) return;
      const account = organization.account || {};
      const next = {
        ...emptyOrganizationForm,
        name: account.name || '',
        mode: account.mode || 'prepaid',
        portal_profile: account.portal_profile || 'standard',
        billing_day: account.billing_day || 1,
        seat_limit: account.seat_limit || 0,
        rpm_limit: account.rpm_limit || 0,
        tpm_limit: account.tpm_limit || 0,
        status: account.status || 'active',
        allowed_groups: organization.allowed_groups || [],
        excluded_models: organization.excluded_models || [],
        model_access_mode: organization.model_access_mode || 'rules',
        log_debug_enabled: organization.log_debug_enabled === true,
        known_models: organization.known_models || [
          ...(organization.allowed_models || []),
          ...(organization.excluded_models || []),
        ],
      };
      setForm(next);
    } else {
      const defaultGroups = groups.length ? [groups[0]] : [];
      const next = { ...emptyOrganizationForm, allowed_groups: defaultGroups };
      setForm(next);
    }
    setHydratedIdentity(editorIdentity);
  }, [open, organization, groups, editorIdentity, hydratedIdentity]);

  const setValue = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));

  const submit = async () => {
    if (saving || !scope.ready || !form.allowed_groups.length) return;
    if (!editing) {
      const validationMessage = validateManagedUserV8(
        {
          username: form.owner_username,
          password: form.owner_password,
          displayName: form.owner_display_name,
          email: form.owner_email,
        },
        { requireUsername: true, requirePassword: true, t }
      );
      if (validationMessage) {
        showError(validationMessage);
        return;
      }
    }
    const rpmLimit = Number(form.rpm_limit);
    const tpmLimit = Number(form.tpm_limit);
    if (
      !Number.isInteger(rpmLimit) ||
      rpmLimit < 0 ||
      !Number.isInteger(tpmLimit) ||
      tpmLimit < 0
    ) {
      showError(t('organization.validation.rate_limits'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        excluded_models: editing
          ? form.excluded_models
          : form.excluded_models.filter((model) => scopeModels.includes(model)),
        billing_day: Number(form.billing_day),
        seat_limit: Number(form.seat_limit),
        rpm_limit: rpmLimit,
        tpm_limit: tpmLimit,
      };
      const response = editing
        ? await API.put(
            `/api/billing/v8/admin/organizations/${organization.account.id}`,
            {
              name: payload.name,
              status: payload.status,
              mode: payload.mode,
              portal_profile: payload.portal_profile,
              billing_day: payload.billing_day,
              seat_limit: payload.seat_limit,
              rpm_limit: payload.rpm_limit,
              tpm_limit: payload.tpm_limit,
              allowed_groups: payload.allowed_groups,
              excluded_models: payload.excluded_models,
              model_access_mode: payload.model_access_mode,
              log_debug_enabled: payload.log_debug_enabled === true,
              known_models: payload.known_models,
            }
          )
        : await API.post('/api/billing/v8/admin/organizations', payload);
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showFormalPricingWarningsV87(response.data?.pricing_warnings, t);
      showSuccess(
        t(
          editing
            ? 'organization.messages.updated'
            : 'organization.messages.created'
        )
      );
      onSaved();
      onClose();
    } catch (error) {
      showError(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeIcon size='large'>
      <Modal.Header>
        {t(editing ? 'organization.edit.title' : 'organization.create.title')}
      </Modal.Header>
      <Modal.Content scrolling>
        <Form>
          <Form.Group widths='equal'>
            <Form.Input
              label={t('organization.fields.name')}
              value={form.name}
              onChange={(_, { value }) => setValue('name', value)}
              required
            />
            <Form.Input
              type='number'
              min='0'
              label={t('organization.fields.seat_limit')}
              value={form.seat_limit}
              onChange={(_, { value }) => setValue('seat_limit', value)}
              required
            />
            {editing ? (
              <Form.Select
                label={t('organization.fields.status')}
                value={form.status}
                options={[
                  {
                    key: 'active',
                    value: 'active',
                    text: t('organization.status.active'),
                  },
                  {
                    key: 'suspended',
                    value: 'suspended',
                    text: t('organization.status.suspended'),
                  },
                ]}
                onChange={(_, { value }) => setValue('status', value)}
              />
            ) : null}
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              type='number'
              min='0'
              step='1'
              inputMode='numeric'
              label={t('organization.fields.rpm_limit')}
              value={form.rpm_limit}
              onChange={(_, { value }) => setValue('rpm_limit', value)}
            />
            <Form.Input
              type='number'
              min='0'
              step='1'
              inputMode='numeric'
              label={t('organization.fields.tpm_limit')}
              value={form.tpm_limit}
              onChange={(_, { value }) => setValue('tpm_limit', value)}
            />
          </Form.Group>
          <Message info content={t('organization.fields.rate_limit_help')} />
          <Form.Field>
            <label>{t('organization.fields.portal_profile')}</label>
            <Button.Group size='small'>
              <Button
                type='button'
                active={form.portal_profile === 'standard'}
                onClick={() => setValue('portal_profile', 'standard')}
              >
                {t('organization.portal.standard')}
              </Button>
              <Button
                type='button'
                active={form.portal_profile === 'vertex_only'}
                onClick={() => setValue('portal_profile', 'vertex_only')}
              >
                <Icon name='google' />
                {t('organization.portal.vertex_only')}
              </Button>
            </Button.Group>
          </Form.Field>
          <Message
            info
            content={t('organization.fields.portal_profile_help')}
          />
          {editing ? (
            <Form.Group widths='equal'>
              <Form.Select
                label={t('organization.fields.mode')}
                value={form.mode}
                options={[
                  {
                    key: 'prepaid',
                    value: 'prepaid',
                    text: t('organization.mode.prepaid'),
                  },
                  {
                    key: 'postpaid',
                    value: 'postpaid',
                    text: t('organization.mode.postpaid'),
                  },
                ]}
                onChange={(_, { value }) => setValue('mode', value)}
              />
              <Form.Input
                type='number'
                min='1'
                max='31'
                label={t('organization.fields.billing_day')}
                value={form.billing_day}
                onChange={(_, { value }) => setValue('billing_day', value)}
              />
            </Form.Group>
          ) : null}
          {editing &&
          (form.mode !== organization?.account?.mode ||
            Number(form.billing_day) !==
              Number(organization?.account?.billing_day)) ? (
            <Message
              warning
              content={t('organization.edit.billing_change_warning')}
            />
          ) : null}
          {!editing ? (
            <>
              <Form.Group widths='equal'>
                <Form.Input
                  label={t('organization.fields.owner_username')}
                  value={form.owner_username}
                  placeholder={t('user.edit.username_placeholder')}
                  error={
                    Boolean(form.owner_username) &&
                    !isValidUsernameV108(form.owner_username.trim()) && {
                      content: t('organization.validation.username_length'),
                    }
                  }
                  onChange={(_, { value }) => setValue('owner_username', value)}
                  required
                />
                <Form.Input
                  label={t('organization.fields.owner_display_name')}
                  value={form.owner_display_name}
                  maxLength={20}
                  onChange={(_, { value }) =>
                    setValue('owner_display_name', value)
                  }
                />
                <Form.Input
                  type='password'
                  autoComplete='new-password'
                  label={t('organization.fields.owner_password')}
                  value={form.owner_password}
                  onChange={(_, { value }) => setValue('owner_password', value)}
                  required
                />
              </Form.Group>
              <PasswordRequirementsV9 password={form.owner_password} />
              <Form.Group widths='equal'>
                <Form.Input
                  type='email'
                  label={t('organization.fields.owner_email')}
                  value={form.owner_email}
                  maxLength={50}
                  onChange={(_, { value }) => setValue('owner_email', value)}
                />
                <Form.Select
                  label={t('organization.fields.mode')}
                  value={form.mode}
                  options={[
                    {
                      key: 'prepaid',
                      value: 'prepaid',
                      text: t('organization.mode.prepaid'),
                    },
                    {
                      key: 'postpaid',
                      value: 'postpaid',
                      text: t('organization.mode.postpaid'),
                    },
                  ]}
                  onChange={(_, { value }) => setValue('mode', value)}
                />
                <Form.Input
                  type='number'
                  min='1'
                  max='31'
                  label={t('organization.fields.billing_day')}
                  value={form.billing_day}
                  onChange={(_, { value }) => setValue('billing_day', value)}
                />
              </Form.Group>
            </>
          ) : null}
          <Form.Field>
            <label>{t('organization.fields.allowed_groups')}</label>
            <Dropdown
              fluid
              multiple
              search
              selection
              options={dropdownOptions(groups)}
              value={form.allowed_groups}
              disabled={saving}
              onChange={(_, { value }) => setValue('allowed_groups', value)}
            />
          </Form.Field>
          <Form.Field>
            <OrganizationModelExclusionsV104
              key={editorIdentity}
              selectionScope={JSON.stringify([...form.allowed_groups].sort())}
              models={scopeModels}
              knownModels={form.known_models}
              exclusions={form.excluded_models}
              mode={organizationModelModeV104(
                form.model_access_mode,
                scopeModels,
                form.excluded_models
              )}
              loading={scope.loading}
              error={scope.error}
              disabled={saving}
              onRefresh={scope.refresh}
              onChange={(value) =>
                setForm((current) => ({
                  ...current,
                  excluded_models: value,
                  model_access_mode: 'rules',
                }))
              }
              onModeChange={(mode) =>
                setForm((current) => ({
                  ...current,
                  model_access_mode: mode,
                  excluded_models:
                    mode === 'all_current'
                      ? allowAllCurrentOrganizationModelsV104(
                          scopeModels,
                          current.excluded_models
                        )
                      : current.excluded_models,
                }))
              }
            />
          </Form.Field>
          <Form.Checkbox
            id='organization-log-debug-v105'
            toggle
            className='organization-log-debug-v105'
            label={t('organization.fields.log_debug_enabled')}
            aria-label={t('organization.fields.log_debug_enabled')}
            checked={form.log_debug_enabled === true}
            disabled={saving}
            onChange={(_, { checked }) =>
              setValue('log_debug_enabled', checked)
            }
          />
          {scope.ready && (
            <Message
              info
              content={t('organization.fields.effective_models_count', {
                count: organizationEffectiveModelCountV812({
                  excludedModels: form.excluded_models,
                  routableModels: scopeModels,
                }),
              })}
            />
          )}
          {!editing && form.mode === 'prepaid' ? (
            <Form.Group widths='equal'>
              <Form.Input
                label={t('organization.fields.opening_credit')}
                value={form.opening_credit_usd}
                onChange={(_, { value }) =>
                  setValue('opening_credit_usd', value)
                }
              />
              <Form.Input
                label={t('organization.fields.idempotency_key')}
                value={form.opening_idempotency_key}
                onChange={(_, { value }) =>
                  setValue('opening_idempotency_key', value)
                }
              />
              <Form.Input
                label={t('organization.fields.description')}
                value={form.opening_description}
                onChange={(_, { value }) =>
                  setValue('opening_description', value)
                }
              />
            </Form.Group>
          ) : null}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>{t('organization.actions.cancel')}</Button>
        <Button
          primary
          icon
          labelPosition='left'
          loading={saving}
          disabled={
            saving ||
            !scope.ready ||
            !form.allowed_groups.length ||
            (!editing && !isValidNewPasswordV9(form.owner_password))
          }
          onClick={submit}
        >
          <Icon name='save' />
          {t('organization.actions.save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const MemberEditor = ({
  open,
  member,
  organization,
  adminOrganizationId = null,
  virtual = false,
  canDelete = false,
  onDelete,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const editing = Boolean(member);
  const owner = member?.role === 'owner';
  const organizationManagedScope = !adminOrganizationId && !virtual && !owner;
  const [form, setForm] = useState(emptyMemberForm);
  const [scopeModels, setScopeModels] = useState([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const allowedGroups = useMemo(
    () => organization?.allowed_groups || [],
    [organization]
  );

  const loadScope = useCallback(
    async (groups, selectedModels, selectAll) => {
      if (virtual) {
        setScopeModels([]);
        setScopeLoading(false);
        return;
      }
      if (!groups.length) {
        setScopeModels([]);
        setForm((current) => ({ ...current, allowed_models: [] }));
        return;
      }
      setScopeLoading(true);
      try {
        const response = await API.get(
          scopePath(
            adminOrganizationId
              ? '/api/billing/v8/admin/organization-scope'
              : '/api/billing/v8/organization/scope',
            groups
          )
        );
        if (!response) return;
        if (!response.data?.success) throw new Error(response.data?.message);
        const models = response.data.data?.models || [];
        setScopeModels(models);
        const modelSet = new Set(models);
        setForm((current) => ({
          ...current,
          allowed_models: selectAll
            ? models
            : (selectedModels || current.allowed_models).filter((item) =>
                modelSet.has(item)
              ),
        }));
      } catch (error) {
        showError(error.message);
      } finally {
        setScopeLoading(false);
      }
    },
    [adminOrganizationId, virtual]
  );

  useEffect(() => {
    if (!open) return;
    setDeleteConfirmation('');
    if (member) {
      const user = member.user || {};
      const accessGroups = organizationManagedScope
        ? allowedGroups
        : member.access_groups || [];
      const next = {
        ...emptyMemberForm,
        display_name: user.display_name || '',
        email: user.email || '',
        status: member.status || 'active',
        primary_group: accessGroups.includes(user.group)
          ? user.group
          : accessGroups[0] || '',
        access_groups: accessGroups,
        allowed_models: member.allowed_models || [],
        model_scope: member.model_scope === 'custom' ? 'custom' : 'inherit',
        cycle_limit_usd: member.cycle_limit_usd ?? '',
        quota_usd: member.quota_usd ?? '',
      };
      next.allowed_models =
        next.model_scope === 'custom'
          ? member.custom_models || member.allowed_models || []
          : [];
      setForm(next);
      loadScope(next.access_groups, next.allowed_models, false);
    } else {
      const accessGroups = organizationManagedScope
        ? allowedGroups
        : allowedGroups.length
        ? [allowedGroups[0]]
        : [];
      const next = {
        ...emptyMemberForm,
        primary_group: accessGroups[0] || '',
        access_groups: accessGroups,
      };
      setForm(next);
      loadScope(accessGroups, [], false);
    }
  }, [open, member, allowedGroups, loadScope, organizationManagedScope]);

  const setValue = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));

  const changeGroups = (_, { value }) => {
    const primary = value.includes(form.primary_group)
      ? form.primary_group
      : value[0] || '';
    setForm((current) => ({
      ...current,
      access_groups: value,
      primary_group: primary,
    }));
    loadScope(
      value,
      form.allowed_models,
      !editing && form.allowed_models.length === 0
    );
  };

  const submit = async () => {
    const validationMessage = validateManagedUserV8(
      {
        username: form.username,
        password: form.password,
        displayName: form.display_name,
        email: form.email,
      },
      { requireUsername: !editing, requirePassword: !editing, t }
    );
    if (validationMessage) {
      showError(validationMessage);
      return;
    }
    setSaving(true);
    try {
      const accessGroups = organizationManagedScope
        ? allowedGroups
        : form.access_groups;
      const payload = {
        ...form,
        primary_group: organizationManagedScope
          ? accessGroups[0] || ''
          : form.primary_group,
        access_groups: owner
          ? organization?.allowed_groups || []
          : accessGroups,
        allowed_models:
          virtual || owner
            ? owner
              ? organization?.allowed_models || []
              : []
            : form.model_scope === 'custom'
            ? form.allowed_models
            : [],
        model_scope: owner ? 'inherit' : form.model_scope,
        cycle_limit_usd:
          owner && (virtual || member.cycle_limit_usd == null)
            ? ''
            : form.cycle_limit_usd,
      };
      if (virtual && !owner) {
        payload.quota_usd = form.quota_usd;
        delete payload.cycle_limit_usd;
      } else {
        delete payload.quota_usd;
      }
      const memberApiBase = adminOrganizationId
        ? `/api/billing/v8/admin/organizations/${adminOrganizationId}/members`
        : '/api/billing/v8/organization/members';
      const response = editing
        ? await API.put(`${memberApiBase}/${member.user.id}`, payload)
        : await API.post(memberApiBase, payload);
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showSuccess(
        t(
          editing
            ? 'organization.messages.member_updated'
            : 'organization.messages.member_created'
        )
      );
      onSaved();
      onClose();
    } catch (error) {
      showError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (!onDelete || deleteConfirmation !== member?.user?.username) return;
    setDeleting(true);
    try {
      const deleted = await onDelete(member);
      if (deleted) onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeIcon size='large'>
      <Modal.Header>
        {t(
          editing
            ? owner
              ? 'organization.members.edit_owner_title'
              : 'organization.members.edit_title'
            : 'organization.members.create_title'
        )}
      </Modal.Header>
      <Modal.Content scrolling>
        <Form>
          {!editing ? (
            <Form.Group widths='equal'>
              <Form.Input
                label={t('organization.fields.username')}
                value={form.username}
                placeholder={t('user.edit.username_placeholder')}
                error={
                  Boolean(form.username) &&
                  !isValidUsernameV108(form.username.trim()) && {
                    content: t('organization.validation.username_length'),
                  }
                }
                onChange={(_, { value }) => setValue('username', value)}
                required
              />
              <Form.Input
                label={t('organization.fields.display_name')}
                value={form.display_name}
                maxLength={20}
                onChange={(_, { value }) => setValue('display_name', value)}
              />
              <Form.Input
                type='password'
                autoComplete='new-password'
                label={t('organization.fields.password')}
                value={form.password}
                onChange={(_, { value }) => setValue('password', value)}
                required
              />
            </Form.Group>
          ) : (
            <Form.Group widths='equal'>
              <Form.Input
                label={t('organization.fields.display_name')}
                value={form.display_name}
                maxLength={20}
                onChange={(_, { value }) => setValue('display_name', value)}
              />
              <Form.Input
                type='password'
                autoComplete='new-password'
                label={t('organization.fields.new_password')}
                value={form.password}
                onChange={(_, { value }) => setValue('password', value)}
              />
              <Form.Select
                label={t('organization.fields.status')}
                value={form.status}
                options={[
                  {
                    key: 'active',
                    value: 'active',
                    text: t('organization.status.active'),
                  },
                  {
                    key: 'suspended',
                    value: 'suspended',
                    text: t('organization.status.suspended'),
                  },
                ]}
                onChange={(_, { value }) => setValue('status', value)}
              />
            </Form.Group>
          )}
          <PasswordRequirementsV9 password={form.password} optional={editing} />
          <Form.Group widths='equal'>
            <Form.Input
              type='email'
              label={t('organization.fields.email')}
              value={form.email}
              maxLength={50}
              onChange={(_, { value }) => setValue('email', value)}
            />
            {!owner || (!virtual && member.cycle_limit_usd != null) ? (
              <Form.Input
                label={t(
                  virtual
                    ? 'organization.fields.root_quota'
                    : 'organization.fields.cycle_limit'
                )}
                type='number'
                min='0'
                step='0.01'
                inputMode='decimal'
                required={owner}
                value={virtual ? form.quota_usd : form.cycle_limit_usd}
                onChange={(_, { value }) =>
                  setValue(virtual ? 'quota_usd' : 'cycle_limit_usd', value)
                }
              />
            ) : null}
          </Form.Group>
          {owner ? (
            <Message info content={t('organization.members.owner_scope')} />
          ) : organizationManagedScope ? null : (
            <>
              <Form.Field>
                <label>{t('organization.fields.access_groups')}</label>
                <Dropdown
                  fluid
                  multiple
                  search
                  selection
                  options={dropdownOptions(allowedGroups)}
                  value={form.access_groups}
                  onChange={changeGroups}
                />
              </Form.Field>
              <Form.Field>
                <label>{t('organization.fields.primary_group')}</label>
                <Dropdown
                  fluid
                  selection
                  options={dropdownOptions(form.access_groups)}
                  value={form.primary_group}
                  onChange={(_, { value }) => setValue('primary_group', value)}
                />
              </Form.Field>
            </>
          )}
          {!virtual && !owner ? (
            <>
              <Form.Field>
                <label>{t('organization.fields.member_model_scope')}</label>
                <Button.Group size='small'>
                  <Button
                    type='button'
                    active={form.model_scope === 'inherit'}
                    onClick={() => setValue('model_scope', 'inherit')}
                  >
                    {t('organization.fields.model_scope_inherit')}
                  </Button>
                  <Button
                    type='button'
                    active={form.model_scope === 'custom'}
                    onClick={() => setValue('model_scope', 'custom')}
                  >
                    {t('organization.fields.model_scope_custom')}
                  </Button>
                </Button.Group>
              </Form.Field>
              {form.model_scope === 'custom' ? (
                <Form.Field>
                  <label>{t('organization.fields.allowed_models')}</label>
                  <Dropdown
                    fluid
                    multiple
                    search
                    selection
                    loading={scopeLoading}
                    options={dropdownOptions(scopeModels)}
                    value={form.allowed_models}
                    onChange={(_, { value }) =>
                      setValue('allowed_models', value)
                    }
                  />
                </Form.Field>
              ) : (
                <Message
                  info
                  content={t('organization.fields.member_scope_help')}
                />
              )}
            </>
          ) : null}
          {editing && canDelete ? (
            <div className='organization-danger-zone-v8'>
              <Header as='h4'>
                {t(
                  owner
                    ? 'organization.danger.organization_title'
                    : 'organization.danger.member_title'
                )}
              </Header>
              <p>
                {t(
                  owner
                    ? 'organization.danger.organization_description'
                    : 'organization.danger.member_description',
                  { username: member.user?.username }
                )}
              </p>
              <Form.Input
                label={t('organization.danger.confirm_label', {
                  username: member.user?.username,
                })}
                value={deleteConfirmation}
                onChange={(_, { value }) => setDeleteConfirmation(value)}
              />
              <Button
                negative
                icon
                labelPosition='left'
                type='button'
                loading={deleting}
                disabled={deleteConfirmation !== member.user?.username}
                onClick={deleteAccount}
              >
                <Icon name='trash' />
                {t(
                  owner
                    ? 'organization.danger.delete_organization'
                    : 'organization.danger.delete_member'
                )}
              </Button>
            </div>
          ) : null}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>{t('organization.actions.cancel')}</Button>
        <Button
          primary
          icon
          labelPosition='left'
          loading={saving}
          disabled={
            saving ||
            (owner &&
              !virtual &&
              member.cycle_limit_usd != null &&
              String(form.cycle_limit_usd).trim() === '') ||
            ((!editing || Boolean(form.password)) &&
              !isValidNewPasswordV9(form.password))
          }
          onClick={submit}
        >
          <Icon name='save' />
          {t('organization.actions.save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const OrganizationCreationInvitationsV109 = ({ onBack }) => {
  const { i18n } = useTranslation();
  const chinese = i18n?.language?.startsWith('zh');
  return (
    <div className='organization-creation-workspace-v109'>
      <div className='organization-toolbar-v8'>
        <Button type='button' onClick={onBack}>
          <Icon name='arrow left' />
          {chinese ? '返回组织' : 'Back to organization'}
        </Button>
      </div>
      <OrganizationInvitationsV108 admin mode='organizationCreation' />
    </div>
  );
};

export const RootOrganizations = () => {
  const { t, i18n } = useTranslation();
  const [creationInvitationsOpen, setCreationInvitationsOpen] = useState(false);
  const creationInvitationLabel = i18n?.language?.startsWith('zh')
    ? '组织创建的邀请链接'
    : 'Organization creation links';
  const sourceLabel = i18n?.language?.startsWith('zh')
    ? 'Source 倍率与可用性'
    : 'Source settings';
  const [sourceOrganization, setSourceOrganization] = useState(null);
  const navigate = useNavigate();
  const [viewState, setViewState] = useSessionViewStateV81('organizations', {
    page: 1,
    pageSize: readPageSizeV8('v8-page-size-organizations', 20),
  });
  const [page, setPage] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'page'
  );
  const [pageSize, setPageSize] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'pageSize'
  );
  const {
    organizations,
    total,
    loading,
    error,
    groups,
    groupsReady,
    groupsError,
    retry: load,
  } = useOrganizationListV1116(page, pageSize);
  const text = (zh, en) => (i18n?.language?.startsWith('zh') ? zh : en);
  const [editor, setEditor] = useState({ open: false, organization: null });

  if (creationInvitationsOpen)
    return (
      <OrganizationCreationInvitationsV109
        onBack={() => setCreationInvitationsOpen(false)}
      />
    );

  return (
    <>
      <div className='organization-toolbar-v8'>
        <div>
          <Header as='h2'>{t('organization.admin.title')}</Header>
          <span className='organization-muted-v8'>
            {Number.isInteger(total)
              ? t('organization.admin.total', { count: total })
              : text('组织列表尚未加载', 'Organization list not loaded')}
          </span>
        </div>
        <div className='organization-invitation-actions-v109'>
          <Button
            primary
            icon
            labelPosition='left'
            disabled={!groupsReady}
            onClick={() => setEditor({ open: true, organization: null })}
          >
            <Icon name='plus' />
            {t('organization.actions.create')}
          </Button>
          <Button
            type='button'
            onClick={() => setCreationInvitationsOpen(true)}
            aria-label={creationInvitationLabel}
          >
            <Icon name='linkify' />
            {creationInvitationLabel}
          </Button>
        </div>
      </div>
      {groupsError && !error && (
        <Message warning role='alert'>
          {text(
            '分组加载失败，组织列表仍可查看。重试后可创建或编辑组织。',
            'Groups could not be loaded. Organizations remain visible; retry to enable creation and editing.'
          )}
          <Button onClick={() => load()}>{text('重试', 'Retry')}</Button>
        </Message>
      )}
      <Segment basic className='organization-table-band-v8'>
        {loading ? (
          <Loader active inline='centered' />
        ) : error ? (
          <Message negative role='alert'>
            <Message.Header>
              {text('组织列表加载失败', 'Could not load organizations')}
            </Message.Header>
            <p>
              {text(
                '请求超时、连接中断或服务暂不可用。组织数据未因此删除，请重试。',
                'The request timed out, was interrupted, or the service is unavailable. This does not mean organizations were deleted. Please retry.'
              )}
            </p>
            <Button onClick={() => load()}>{text('重试', 'Retry')}</Button>
          </Message>
        ) : organizations.length === 0 ? (
          <Message info content={t('organization.empty')} />
        ) : (
          <div
            className='management-table-scroll-v72'
            role='region'
            tabIndex={0}
          >
            <Table
              basic='very'
              compact
              unstackable
              className='management-table-v72'
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {t('organization.table.name')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.table.owner')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.table.mode')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.table.portal')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.table.credit')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.table.seats')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.table.scope')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.table.status')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.table.actions')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>{sourceLabel}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {organizations.map((organization) => {
                  const account = organization.account || {};
                  const virtual = Boolean(organization.virtual);
                  const creditMetric =
                    getOrganizationCreditMetricV86(organization);
                  return (
                    <Table.Row key={account.id}>
                      <Table.Cell>
                        <strong>{account.name}</strong>
                        {virtual ? (
                          <Label
                            basic
                            size='tiny'
                            className='organization-owner-label-v8'
                          >
                            {t('organization.virtual', {
                              defaultValue: 'System',
                            })}
                          </Label>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell>{organization.owner?.username}</Table.Cell>
                      <Table.Cell>
                        {virtual
                          ? t('organization.mode.system', {
                              defaultValue: 'System',
                            })
                          : t(`organization.mode.${account.mode}`)}
                      </Table.Cell>
                      <Table.Cell>
                        {virtual
                          ? '-'
                          : t(
                              `organization.portal.${
                                account.portal_profile || 'standard'
                              }`
                            )}
                      </Table.Cell>
                      <Table.Cell>
                        {creditMetric ? (
                          <div className='organization-credit-cell-v86'>
                            <strong>
                              {creditMetric.amount == null
                                ? '-'
                                : `USD ${creditMetric.amount}`}
                            </strong>
                            <span>{t(creditMetric.labelKey)}</span>
                          </div>
                        ) : (
                          '-'
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {virtual
                          ? `${account.seat_used} / ∞`
                          : `${account.seat_used} / ${account.seat_limit}`}
                      </Table.Cell>
                      <Table.Cell>
                        {virtual ? (
                          '-'
                        ) : (
                          <>
                            <Label basic>
                              {t('organization.table.groups_count', {
                                count: organization.allowed_groups?.length || 0,
                              })}
                            </Label>
                          </>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Label
                          color={account.status === 'active' ? 'green' : 'red'}
                          basic
                        >
                          {t(`organization.status.${account.status}`)}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        <div className='organization-action-group-v8'>
                          <Button
                            icon
                            labelPosition='left'
                            size='small'
                            title={t('organization.actions.open_accounts')}
                            onClick={() =>
                              navigate(`/organization/${account.id}`)
                            }
                          >
                            <Icon name='folder open outline' />
                            {t('organization.actions.open_accounts')}
                          </Button>
                          {!virtual ? (
                            <Button
                              icon
                              labelPosition='left'
                              size='small'
                              title={t('organization.actions.edit')}
                              disabled={!groupsReady}
                              onClick={() =>
                                setEditor({ open: true, organization })
                              }
                            >
                              <Icon name='edit' />
                              {t('organization.actions.edit')}
                            </Button>
                          ) : null}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        {!virtual ? (
                          <Button
                            icon
                            basic
                            size='small'
                            title={sourceLabel}
                            aria-label={`${account.name} ${sourceLabel}`}
                            onClick={() => setSourceOrganization(organization)}
                          >
                            <Icon name='sliders horizontal' />
                          </Button>
                        ) : (
                          '-'
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        )}
      </Segment>
      <div className='list-pagination-v8'>
        <PageSizeSelectV8
          label={t('log.table.page_size')}
          storageKey='v8-page-size-organizations'
          value={pageSize}
          onChange={(nextPageSize) => {
            setPage(1);
            setPageSize(nextPageSize);
          }}
        />
        {total > pageSize ? (
          <Pagination
            activePage={page}
            totalPages={Math.ceil(total / pageSize)}
            onPageChange={(_, { activePage }) => setPage(Number(activePage))}
          />
        ) : null}
      </div>
      <OrganizationEditor
        open={editor.open}
        organization={editor.organization}
        groups={groups}
        onClose={() => setEditor({ open: false, organization: null })}
        onSaved={() => load(page)}
      />
      {sourceOrganization && (
        <OrganizationSourcesV109
          organization={sourceOrganization}
          onClose={() => setSourceOrganization(null)}
        />
      )}
    </>
  );
};

const currentUserIdV8 = () => {
  try {
    return Number(JSON.parse(localStorage.getItem('user') || '{}')?.id || 0);
  } catch (_) {
    return 0;
  }
};

const ManagedOrganization = ({ organizationId, selectedUserId }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const returnToOrganizations = useSmartBackV81('/organization');
  const returnToAccounts = useSmartBackV81(`/organization/${organizationId}`);
  const rootAdmin = useMemo(() => isRoot(), []);
  const [creationInvitationsOpen, setCreationInvitationsOpen] = useState(false);
  const creationInvitationLabel = i18n?.language?.startsWith('zh')
    ? '组织创建的邀请链接'
    : 'Organization creation links';
  const [payload, setPayload] = useState(null);
  const [invitationSummary, setInvitationSummary] = useState(null);
  const [invitationSummaryError, setInvitationSummaryError] = useState('');
  const [invitationSummaryRetry, setInvitationSummaryRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false, member: null });
  const [organizationEditorOpen, setOrganizationEditorOpen] = useState(false);
  const [configuredGroups, setConfiguredGroups] = useState([]);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [viewState, setViewState] = useSessionViewStateV81(
    `organization.${organizationId}.${selectedUserId || 'accounts'}`,
    { activeView: 'accounts' }
  );
  const [activeView, setActiveView] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'activeView'
  );
  const invitationLoadError = i18n?.language?.startsWith('zh')
    ? '席位状态加载失败，请重试'
    : 'Could not load seat availability. Try again.';
  const receiveInvitationSummary = useCallback((summary) => {
    setInvitationSummary(summary);
    setInvitationSummaryError('');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = rootAdmin
        ? `/api/billing/v8/admin/organizations/${organizationId}`
        : '/api/billing/v8/organization';
      const response = await API.get(endpoint);
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      const nextPayload = response.data.data;
      if (rootAdmin) {
        const groupResponse = await API.get('/api/group/');
        if (!groupResponse?.data?.success)
          throw new Error(groupResponse?.data?.message);
        setConfiguredGroups(groupResponse.data.data || []);
      }
      setPayload(nextPayload);
      setInvitationSummary(null);
      if (
        !rootAdmin &&
        !selectedUserId &&
        nextPayload?.membership?.role !== 'owner'
      ) {
        const accountID = nextPayload.organization?.account?.id;
        const userID = nextPayload.membership?.user_id;
        if (accountID && userID) {
          navigate(`/organization/${accountID}/user/${userID}`, {
            replace: true,
          });
        }
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  }, [navigate, organizationId, rootAdmin, selectedUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const previousCreditView = useRef(activeView);
  useEffect(() => {
    if (
      previousCreditView.current === 'invitations' &&
      activeView !== 'invitations'
    ) {
      load();
    }
    previousCreditView.current = activeView;
  }, [activeView, load]);

  useEffect(() => {
    const accountID = payload?.organization?.account?.id;
    if (
      loading ||
      creationInvitationsOpen ||
      selectedUserId ||
      activeView === 'invitations' ||
      !payload?.configured ||
      !payload.can_manage ||
      !accountID ||
      payload.virtual ||
      payload.organization?.virtual ||
      (!rootAdmin && payload.membership?.role !== 'owner')
    )
      return;
    let current = true;
    setInvitationSummary(null);
    setInvitationSummaryError('');
    const base = rootAdmin
      ? `/api/billing/v8/admin/organizations/${accountID}/invitations`
      : '/api/billing/v8/organization/invitations';
    const loadAvailability = async () => {
      try {
        const response = await API.get(`${base}?page=1&page_size=1`);
        if (!current) return;
        const summary = response?.data?.data?.summary;
        if (!response?.data?.success || !Number.isInteger(summary?.available))
          throw new Error(response?.data?.message || invitationLoadError);
        receiveInvitationSummary(summary);
      } catch (error) {
        if (current)
          setInvitationSummaryError(error.message || invitationLoadError);
      }
    };
    loadAvailability();
    return () => {
      current = false;
    };
  }, [
    activeView,
    creationInvitationsOpen,
    invitationLoadError,
    invitationSummaryRetry,
    loading,
    payload,
    receiveInvitationSummary,
    rootAdmin,
    selectedUserId,
  ]);

  const deleteMember = async (member) => {
    try {
      const accountID = payload.organization?.account?.id;
      let response;
      if (member.role === 'owner') {
        if (!rootAdmin || payload.virtual) return false;
        response = await API.delete(
          `/api/billing/v8/admin/organizations/${accountID}`
        );
      } else {
        const memberBase = rootAdmin
          ? `/api/billing/v8/admin/organizations/${accountID}/members`
          : '/api/billing/v8/organization/members';
        response = await API.delete(`${memberBase}/${member.user.id}`);
      }
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      if (member.role === 'owner') {
        showSuccess(t('organization.messages.organization_deleted'));
        navigate('/organization');
      } else {
        showSuccess(t('organization.messages.member_deleted'));
        await load();
      }
      return true;
    } catch (error) {
      showError(error.message);
      return false;
    }
  };

  if (rootAdmin && creationInvitationsOpen)
    return (
      <OrganizationCreationInvitationsV109
        onBack={() => setCreationInvitationsOpen(false)}
      />
    );
  if (loading) return <Loader active inline='centered' />;
  if (!payload?.configured)
    return <Message info content={t('organization.not_configured')} />;

  const organization = payload.organization || {};
  const account = organization.account || {};
  const virtual = Boolean(payload.virtual || organization.virtual);
  const canManage = Boolean(payload.can_manage);
  const canInvite =
    canManage &&
    !virtual &&
    Boolean(account.id) &&
    (rootAdmin || payload.membership?.role === 'owner');
  const invitationLabel = i18n?.language?.startsWith('zh')
    ? '邀请码'
    : 'Invitation codes';
  const members = payload.members || [];
  const virtualSubaccountCount = members.filter(
    (member) => member.role !== 'owner'
  ).length;
  const currentUserID = currentUserIdV8();
  const selectedMember = selectedUserId
    ? members.find(
        (member) => String(member.user?.id) === String(selectedUserId)
      )
    : null;

  if (selectedUserId) {
    if (!selectedMember) {
      return (
        <Message
          warning
          content={t('organization.member_not_found', {
            defaultValue: 'Account is not available in this organization.',
          })}
        />
      );
    }
    if (!rootAdmin && selectedMember.user?.id !== currentUserID) {
      return (
        <>
          <Message warning content={t('organization.tokens_private')} />
          <Button icon labelPosition='left' onClick={returnToAccounts}>
            <Icon name='arrow left' />
            {t('organization.accounts_back')}
          </Button>
        </>
      );
    }
    const basePath = `/organization/${account.id}/user/${selectedMember.user.id}`;
    const canReturnToAccounts =
      rootAdmin || payload.membership?.role === 'owner';
    return (
      <>
        <div className='organization-toolbar-v8'>
          <div>
            <Header as='h2'>{selectedMember.user?.username}</Header>
            <span className='organization-muted-v8'>
              {selectedMember.role === 'owner'
                ? t('organization.role.owner')
                : t('organization.role.member')}
            </span>
          </div>
          {canReturnToAccounts ? (
            <Button icon labelPosition='left' onClick={returnToAccounts}>
              <Icon name='arrow left' />
              {t('organization.accounts_back', { defaultValue: 'Accounts' })}
            </Button>
          ) : null}
        </div>
        <MemberUsageSummaryV97 member={selectedMember} virtual={virtual} />
        <Button.Group size='small' className='organization-view-control-v8'>
          <Button
            active={activeView === 'accounts'}
            onClick={() => setActiveView('accounts')}
          >
            <Icon name='key' />
            {t('organization.usage.view_tokens')}
          </Button>
          <Button
            active={activeView === 'usage'}
            onClick={() => setActiveView('usage')}
          >
            <Icon name='chart bar outline' />
            {t('organization.usage.view_usage')}
          </Button>
        </Button.Group>
        {activeView === 'usage' ? (
          <UsageAuditV8
            admin={rootAdmin}
            organizationId={account.id}
            members={[selectedMember]}
            initialUserId={selectedMember.user.id}
            currentUserId={currentUserID}
            billingDay={account.billing_day}
          />
        ) : (
          <Segment basic className='organization-token-band-v8'>
            <TokensTable
              key={selectedMember.user.id}
              targetUserId={selectedMember.user.id}
              basePath={basePath}
            />
          </Segment>
        )}
      </>
    );
  }

  return (
    <>
      <div className='organization-toolbar-v8'>
        <div>
          <Header as='h2'>{account.name}</Header>
          <span className='organization-muted-v8'>
            {t('organization.accounts_subtitle', {
              defaultValue: 'Accounts and Token ownership',
            })}
          </span>
        </div>
        <div className='organization-invitation-actions-v109'>
          {rootAdmin && !virtual ? (
            <>
              <Button
                icon
                labelPosition='left'
                onClick={() => setOrganizationEditorOpen(true)}
              >
                <Icon name='settings' />
                {t('organization.actions.billing_settings')}
              </Button>
              <Button
                icon
                labelPosition='left'
                onClick={() => setAdjustmentOpen(true)}
              >
                <Icon name='dollar sign' />
                {t('organization.actions.adjust_credit')}
              </Button>
            </>
          ) : null}
          {rootAdmin ? (
            <Button
              icon
              labelPosition='left'
              onClick={returnToOrganizations}
              title={t('organization.actions.back', { defaultValue: 'Back' })}
            >
              <Icon name='arrow left' />
              {t('organization.actions.back', { defaultValue: 'Back' })}
            </Button>
          ) : null}
          {canManage ? (
            <Button
              primary
              icon
              labelPosition='left'
              disabled={
                !virtual &&
                (!Number.isInteger(invitationSummary?.available) ||
                  invitationSummary.available <= 0)
              }
              onClick={() => setEditor({ open: true, member: null })}
            >
              <Icon name='user plus' />
              {t('organization.actions.add_member')}
            </Button>
          ) : null}
          {canInvite ? (
            <Button
              icon
              labelPosition='left'
              active={activeView === 'invitations'}
              onClick={() => setActiveView('invitations')}
              aria-label={invitationLabel}
            >
              <Icon name='ticket' />
              {invitationLabel}
            </Button>
          ) : null}
          {rootAdmin ? (
            <Button
              type='button'
              onClick={() => setCreationInvitationsOpen(true)}
              aria-label={creationInvitationLabel}
            >
              <Icon name='linkify' />
              {creationInvitationLabel}
            </Button>
          ) : null}
        </div>
      </div>
      {canInvite && invitationSummaryError && activeView !== 'invitations' ? (
        <Message negative role='alert'>
          <p>{invitationSummaryError}</p>
          <Button
            size='small'
            onClick={() => setInvitationSummaryRetry((value) => value + 1)}
          >
            <Icon name='refresh' />
            {i18n?.language?.startsWith('zh')
              ? '重试席位状态'
              : 'Retry seat availability'}
          </Button>
        </Message>
      ) : null}
      <Segment basic className='organization-summary-v8'>
        <Statistic.Group widths='four' size='small'>
          <Statistic
            label={t('organization.summary.seats')}
            value={
              virtual
                ? virtualSubaccountCount
                : `${invitationSummary?.seat_used ?? account.seat_used}/${
                    invitationSummary?.seat_limit ?? account.seat_limit
                  }`
            }
          />
          <Statistic
            label={t('organization.summary.groups')}
            value={organizationGroupSummaryV813(organization)}
          />
          <Statistic
            label={t('organization.summary.billing_day')}
            value={virtual ? '-' : account.billing_day}
          />
          <Statistic
            label={t('organization.summary.billing_mode')}
            value={
              virtual ? '-' : t(`billing.mode.${account.mode || 'prepaid'}`)
            }
          />
        </Statistic.Group>
      </Segment>
      <Button.Group size='small' className='organization-view-control-v8'>
        <Button
          active={activeView === 'accounts'}
          onClick={() => setActiveView('accounts')}
        >
          <Icon name='users' />
          {t('organization.usage.view_accounts')}
        </Button>
        <Button
          active={activeView === 'usage'}
          onClick={() => setActiveView('usage')}
        >
          <Icon name='chart bar outline' />
          {t('organization.usage.view_usage')}
        </Button>
        {!virtual ? (
          <Button
            active={activeView === 'billing'}
            onClick={() => setActiveView('billing')}
          >
            <Icon name='book' />
            {t('organization.billing.view_book')}
          </Button>
        ) : null}
        {rootAdmin && !virtual ? (
          <Button
            active={activeView === 'discounts'}
            onClick={() => setActiveView('discounts')}
          >
            <Icon name='percent' />
            {t('organization.billing.view_discounts_v814')}
          </Button>
        ) : null}
      </Button.Group>
      {activeView === 'invitations' && canInvite ? (
        <OrganizationInvitationsV108
          admin={rootAdmin}
          organizationId={account.id}
          onSummaryChange={receiveInvitationSummary}
        />
      ) : activeView === 'usage' ? (
        <UsageAuditV8
          admin={rootAdmin}
          organizationId={account.id}
          members={members}
          currentUserId={currentUserID}
          billingDay={account.billing_day}
        />
      ) : activeView === 'discounts' && rootAdmin && !virtual ? (
        <Segment basic className='organization-discount-band-v814'>
          <BillingDiscountProgramSettingV816
            accountId={account.id}
            organizationName={account.name}
          />
        </Segment>
      ) : activeView === 'billing' && !virtual ? (
        <OrganizationBillingBookV8
          admin={rootAdmin}
          accountId={account.id}
          billingDay={account.billing_day}
        />
      ) : (
        <>
          <div
            className='management-table-scroll-v72'
            role='region'
            tabIndex={0}
          >
            <Table
              basic='very'
              compact
              unstackable
              className='management-table-v72'
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {t('organization.members.user')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.members.role')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.members.groups')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.members.models')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t(
                      virtual
                        ? 'organization.members.remaining_limit'
                        : 'organization.members.limit'
                    )}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.members.total_used')}
                  </Table.HeaderCell>
                  {!virtual && (
                    <Table.HeaderCell>
                      {t('organization.members.cycle_used')}
                    </Table.HeaderCell>
                  )}
                  <Table.HeaderCell>
                    {t('organization.members.available')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.members.status')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('organization.table.actions')}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {members.map((member) => {
                  const ownAccount = member.user?.id === currentUserID;
                  return (
                    <Table.Row
                      key={member.id}
                      className={
                        member.role === 'owner'
                          ? 'organization-owner-row-v8'
                          : ''
                      }
                    >
                      <Table.Cell>
                        <strong>{member.user?.username}</strong>
                        {ownAccount ? (
                          <Label
                            basic
                            size='tiny'
                            className='organization-owner-label-v8'
                          >
                            {t('user.table.current_account')}
                          </Label>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell>
                        <Label
                          basic
                          color={member.role === 'owner' ? 'blue' : undefined}
                        >
                          {t(`organization.role.${member.role}`)}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        {organizationAccessGroupsLabelV813(member)}
                      </Table.Cell>
                      <Table.Cell>
                        {organizationAllowedModelsLabelV813(member)}
                      </Table.Cell>
                      <Table.Cell>
                        {virtual
                          ? member.role === 'owner' || member.quota_usd == null
                            ? t('organization.unlimited')
                            : '$' + member.quota_usd
                          : member.cycle_limit_usd == null
                          ? t('organization.unlimited')
                          : '$' + member.cycle_limit_usd}
                      </Table.Cell>
                      <MemberUsageCellsV97 member={member} virtual={virtual} />
                      <Table.Cell>
                        <Label
                          basic
                          color={member.status === 'active' ? 'green' : 'red'}
                        >
                          {t(`organization.status.${member.status}`)}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        <div className='organization-action-group-v8'>
                          {rootAdmin || ownAccount ? (
                            <Button
                              icon
                              labelPosition='left'
                              size='small'
                              title={t('organization.actions.manage_tokens')}
                              onClick={() =>
                                navigate(
                                  `/organization/${account.id}/user/${member.user.id}`
                                )
                              }
                            >
                              <Icon name='key' />
                              {t('organization.actions.manage_tokens')}
                            </Button>
                          ) : null}
                          {canManage &&
                          (member.role !== 'owner' ||
                            (rootAdmin && !virtual)) ? (
                            <Button
                              icon
                              labelPosition='left'
                              size='small'
                              title={t('organization.actions.edit')}
                              onClick={() => setEditor({ open: true, member })}
                            >
                              <Icon name='edit' />
                              {t('organization.actions.edit')}
                            </Button>
                          ) : null}
                          {canManage &&
                          member.role !== 'owner' &&
                          member.status === 'active' ? (
                            <MemberCreditV108
                              member={member}
                              accountId={account.id}
                              admin={rootAdmin}
                              virtual={virtual}
                              onSaved={load}
                            />
                          ) : null}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        </>
      )}
      {rootAdmin && !virtual ? (
        <OrganizationEditor
          open={organizationEditorOpen}
          organization={organization}
          groups={configuredGroups}
          onClose={() => setOrganizationEditorOpen(false)}
          onSaved={async () => {
            setOrganizationEditorOpen(false);
            await load();
          }}
        />
      ) : null}
      <MemberEditor
        open={editor.open}
        member={editor.member}
        organization={organization}
        adminOrganizationId={rootAdmin ? account.id : null}
        virtual={virtual}
        canDelete={
          Boolean(editor.member) &&
          (editor.member?.role !== 'owner' || (rootAdmin && !virtual))
        }
        onDelete={deleteMember}
        onClose={() => setEditor({ open: false, member: null })}
        onSaved={load}
      />
      <BillingAdjustmentModal
        open={adjustmentOpen}
        organization={organization}
        onClose={() => setAdjustmentOpen(false)}
      />
    </>
  );
};

const Organization = () => {
  const root = useMemo(() => isRoot(), []);
  const { organizationId, userId } = useParams();
  return (
    <div className='dashboard-container management-container-v4 organization-page-v8'>
      {root && !organizationId ? (
        <RootOrganizations />
      ) : (
        <ManagedOrganization
          key={`${organizationId}:${userId || 'accounts'}`}
          organizationId={organizationId}
          selectedUserId={userId}
        />
      )}
    </div>
  );
};

export default Organization;
